import { FunctionComponent as F } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { IState } from "../../core/store/store";
import EditableInput from "../UI/Input/EditableInput";
import EditableDropdown from "../UI/Input/EditableDropdown";
import { DraggableProvided } from "react-beautiful-dnd";
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { PRODUCTS, SAVINGS, SIMILAR } from "../../core/router/paths";
import TableCell from "@mui/material/TableCell";
import { updateChecked } from "../../core/store/protocol/protocol.slice";
import { validateDaysPerMonth, validateDose, validateDosesPerDay } from "../../utils/validate";
import CostPerMonth from "./CostPerMonth";
import ProductsPerMonth from "./ProductsPerMonth";
import SwapVendor, { SwapVendorProps } from "../UI/Button/SwapVendor";
import TextTooltip from "../UI/Tooltip/TextTooltip";
import { Typography, Stack, Checkbox, IconButton, Box, TableRow } from "@mui/material";
import Discounts from "../UI/Button/Discounts";
import { useAppDispatch } from "../../core/store/hooks";
import { toggleDiscountsAsync } from "../../core/store/protocol/protocol.thunks";
import FormIcon, { FormIconDosing } from "../UI/Icons/FormIcon";
import DosingNote from "../UI/Button/DosingNote";
import LinkOpt from "../UI/Icons/LinkOpt";
import { max2Sf } from "../../utils/string";
import { ProtocolDosingRow } from "@protoplan/types";


interface Props
{
  protocolId: number,
  rowIndex: number,
  withinBudgetColour: string,
  headers: { accessor: string, Header: string }[],
  updateValue: (dosingId: number, value: number, label: keyof ProtocolDosingRow) => Promise<void>;
  provided?: DraggableProvided
}

const ProtocolTableRow: F<Props> = props =>
{
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const protocolEdit = useSelector((state: IState) => state.protocol.protocols
    .find(p =>  p.protocolId === props.protocolId)?.edit);
  const dosing = useSelector((state: IState) =>
  {
    const dosings = state.protocol.protocols.find(p =>  p.protocolId === props.protocolId)?.dosings;
    if(dosings)
      return dosings[props.rowIndex];
  });

  const edit = protocolEdit === undefined || protocolEdit;
  const dosingId = dosing?.dosingId;

  const checkedState = useSelector((state: IState) =>
  {
    const dosings = state.protocol.protocols.find(p =>  p.protocolId === props.protocolId)?.dosings;
    if(dosings)
      return dosings[props.rowIndex].checked;
  });
  const checked = checkedState !== undefined && checkedState;

  const dosings = useSelector((state: IState) => state.protocol.protocols
    .find(p =>  p.protocolId === props.protocolId)?.dosings);

  if(!dosing)
    return <TableRow key={props.rowIndex}/>;

  const handleCheck = () =>
  {
    dosings?.forEach(d =>
    {
      if(d.bundleId ? d.bundleId === dosing.bundleId : d.dosingId === dosing.dosingId)
        dispatch(updateChecked({ protocolId: props.protocolId, ...d }));
    });
  };

  const updateValue = (label: keyof ProtocolDosingRow) => async(value: number) =>
  {
    if(!dosingId)
      return;

    props.updateValue(dosingId, value, label);
  };

  const handleFormClick = () =>
  {
    const productId = dosing.productId;
    if(dosing.productId)
      navigate(PRODUCTS + "/" + productId + "/" + SIMILAR, { state: { dosingId } });
    else
      navigate(PRODUCTS, { state: { swapDosingId: dosing.dosingId, suppId: dosing.suppId } });
  };

  const supplementInfo = () =>
  {
    if(!dosing.amount || !dosing.amountUnit)
      return;

    const amount = dosing.amount + " " + dosing.amountUnit;

    if(!dosing.bundleId)
      return amount;

    const quantity = ((dosing.quantity || 1) > 1) ? "x" + dosing.quantity + " " : "";
    const listingName = (dosing.listingName && dosing.listingName.length >= 40) ?
      dosing.listingName.slice(0, 37) + "..." :
      dosing.listingName;
    const bundleInfo = quantity + "from " + listingName;

    return amount + ", " + bundleInfo;
  }

  return (
    <TableRow
      ref={props.provided?.innerRef}
      {...(props.provided?.draggableProps || {})}
      {...(props.provided?.dragHandleProps || {})}>
      {props.headers.map((header, index) =>
      {
        const label = header.accessor;

        switch(label)
        {
        case "supplement":
          return(
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 270, width: 270, paddingLeft: 10, paddingRight: 10, paddingBottom: 3 }}>
              <Stack direction='row' spacing={1} >
                <Typography variant='body2' display='inline' id='Supplement'>
                  {dosing.productName || dosing.suppName}
                </Typography>
                <Typography variant='body2' color='gray' sx={{ pt: 0.1, fontSize: 12 }} id='Amount'>
                  {supplementInfo()}
                </Typography>
                {(!dosing.bundleId && edit) ?
                  <TextTooltip lines={[dosing.productId ? "Swap for similar product" : "Choose product"]}>
                    <div onClick={handleFormClick} style={{ height: 24 }}>
                      <FormIcon dosing={dosing as FormIconDosing}/>
                    </div>
                  </TextTooltip> :
                  <></>}
              </Stack>
            </TableCell>);
        case "brandName":
          return(
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 100, width: 100, paddingLeft: 10, paddingRight: 10, paddingBottom: 7 }}>
              <Typography variant='body2'>
                {dosing.brandName}
              </Typography>
            </TableCell>);
        case "vendorName":
          return(
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 150, width: 150, paddingLeft: 10, paddingRight: 10, paddingBottom: 7 }}>
              <Stack direction='row' spacing={1}>
                <Typography id='Vendor' variant='body2'>
                  {dosing.vendorName}
                </Typography>
                <LinkOpt url={dosing.url}/>
                {(!dosing.bundleId && dosing.productId && edit) ?
                  <Box id='SwapVendor' sx={{ display: 'flex', height: 20 }} alignItems='center'>
                    {(dosing.listingSavings?.length || dosing.bundleSavings?.length) ?
                      <TextTooltip lines={["See potential savings"]}>
                        <IconButton
                          size='small'
                          onClick={() => navigate(SAVINGS, { state: { protocolRow: dosing } })}>
                          <MoneyOffIcon style={{ fontSize: 19, cursor: 'pointer', color: 'gray' }}/>
                        </IconButton>
                      </TextTooltip> :
                      <SwapVendor {...({ product: {
                        ...dosing,
                        deliverableListingsForUser: true } } as SwapVendorProps)}/>}
                  </Box> :
                  <></>}
              </Stack>
            </TableCell>);
        case "dose":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 40, width: 40, paddingLeft: 10, paddingRight: 10 }}>
              <EditableInput
                key={index}
                id="Dose"
                initialValue={max2Sf(dosing.dose)}
                type='number'
                autoSelect={true}
                disabled={!edit}
                error={value => !validateDose(value)}
                saveValue={updateValue(label)}/>
            </TableCell>);
        case "doseUnit":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 150, width: 150, paddingLeft: 10, paddingRight: 10 }}>
              <EditableDropdown
                id="DoseUnit"
                initialValue={dosing.doseUnitId || undefined}
                disabled={!edit}
                options={dosing.unitOptions}
                onChange={updateValue("doseUnitId")} />
            </TableCell>);
        case "dosesPerDay":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 35, width: 35, paddingLeft: 10, paddingRight: 0 }}>
              <EditableInput
                key={index}
                initialValue={dosing.dosesPerDay}
                type='number'
                autoSelect={true}
                disabled={!edit}
                error={value => !validateDosesPerDay(value)}
                saveValue={updateValue(label)} />
            </TableCell>);
        case "daysPerMonth":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 40, width: 40, paddingLeft: 10, paddingRight: 10 }}>
              <EditableInput
                key={index}
                initialValue={dosing.daysPerMonth}
                type='number'
                autoSelect={true}
                disabled={!edit}
                error={value => !validateDaysPerMonth(value)}
                saveValue={updateValue(label)} />
            </TableCell>);
        case "productsPerMonth":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 37, width: 37, paddingLeft: 0, paddingRight: 10 }}>
              <ProductsPerMonth row={dosing} colour={props.withinBudgetColour}/>
            </TableCell>);
        case "costPerMonth":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 70, width: 70, paddingLeft: 0, paddingRight: 10 }}>
              <CostPerMonth
                protocolId={props.protocolId}
                dosingId={dosing.dosingId}
                colour={props.withinBudgetColour}/>
            </TableCell>);
        case "discounts":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 23, width: 23, paddingLeft: 0, paddingRight: 10 }}>
              {dosing.discounts?.length ?
                <Discounts
                  protocolId={props.protocolId}
                  dosingId={dosing.dosingId}
                  disabled={!edit}
                  toggleDiscountsAsync={(discountIds: number[]) => appDispatch(toggleDiscountsAsync(
                    props.protocolId,
                    discountIds.map(discountId => ({
                      dosingId: dosing.dosingId,
                      discountId,
                      bundleId: dosing.bundleId || undefined }))))}/> :
                <></>}
            </TableCell>);
        case "checked":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ minWidth: 3, width: 3, paddingLeft: 0, paddingRight: 10, paddingBottom: 7 }}>
              <Checkbox
                style={{ height: 15 }}
                checked={checked}
                onChange={handleCheck}/>
            </TableCell>);
        case "note":
          return (
            <TableCell
              size='small'
              key={index}
              style={{ paddingLeft: 0, paddingBottom: 7 }}>
              <DosingNote protocolId={props.protocolId} dosingId={dosing.dosingId} rowIndex={props.rowIndex}/>
            </TableCell>);
        }
      })}
    </TableRow>);
};




export default ProtocolTableRow;
